import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { AppContent, AppPage, useFeatureToggle } from '@css-xp/base';
import { ScreenEventAnalytics } from '@css-xp/ppc-common';
import { useTitle } from '@css-xp/ppc-custom-hooks';
import {
  SubscriptionTableFlatContainer,
  DownloadCSVPerfV2,
  IncludedProductsSidebar,
} from '@css-xp/ppc-subscription-table';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { Background } from '@css-xp/page-background/src/background-image';
import {
  PPC_PAGE_TITLE,
  ENABLE_DESIGN_ITERATION_1,
  routePaths,
  STATSIG_FEATURE_GATES,
  SUBSCRIPTION_CONSTANTS,
} from '@css-xp/ppc-constants';
import { FilterOption, SortType, Maybe } from '@css-xp/graphtypes/src/generated-types-new';
import { getFilterOptions } from '@css-xp/ppc-subscription-table/src/subscription-table-flat';
import { SUBSCRIPTIONS } from './constants/subscriptions';
import './subscription-page-v2.scss';
//need to be removed once M2.0 Feature flag is removed
import './subscription-page.scss';

const allowedSidebars = {
  [SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_PAGE_SIDEBAR.INCLUDED_PRODUCTS]: IncludedProductsSidebar,
};

export const SubscriptionsPage = () => {
  const { txaId } = useParams<{ txaId: string }>();
  useTitle(PPC_PAGE_TITLE.SUBSCRIPTIONS);
  const [csvFilterOptions, setCsvFilterOptions] = useState<FilterOption[]>(getFilterOptions());
  const [sortOptions, setSortOptions] = useState<Maybe<SortType>>();

  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isFutureDatedSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SUPPORT_FOR_FUTURE_DATED_ENTITLEMENTS
  );
  const isInternalUsageLicensesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_INTERNAL_USAGE_LICENSES_SUPPORT);
  const isIULPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_IUL_PHASE_2);
  const isTWCPhase1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_TWC_SUPPORT_PHASE_1);

  const renderActions = () => {
    return (
      <DownloadCSVPerfV2
        transactionAccountId={txaId}
        buttonAppearance="primary"
        buttonText="Export as CSV"
        className="download-csv-btn"
        isDarkTheme={true}
        filterOptions={csvFilterOptions}
        sortOptions={sortOptions}
      />
    );
  };

  const renderAppPage = () => {
    return (
      <AppPage className="app-page-v2 subscription-page" allowedSidebars={allowedSidebars}>
        <AppContent>
          <ScreenEventAnalytics
            event={{
              name: AnalyticsEventSource.PARTNER_SUBSCRIPTIONS_SCREEN,
              attributes: {},
            }}
          />
          <section className="ph-50 mb-50 subscription-page-table">
            <SubscriptionTableFlatContainer
              transactionAccountId={txaId}
              shouldHideDownloadCsv={!isDesignIteration1Enabled}
              updateCsvFilterOptions={setCsvFilterOptions}
              updateCsvSortOptions={setSortOptions}
              {...(isDesignIteration1Enabled && { shouldHideCustomiseView: true, shouldHideTabs: true })}
              isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
              isSupportForExternallyBilledSubscriptionsEnabled={true}
              isFutureDatedSubscriptionsEnabled={isFutureDatedSubscriptionsEnabled}
              isInternalUsageLicensesEnabled={isInternalUsageLicensesEnabled}
              isTWCPhase1Enabled={isTWCPhase1Enabled}
            />
          </section>
        </AppContent>
      </AppPage>
    );
  };

  const renderPage = () => {
    if (isDesignIteration1Enabled) {
      const breadcrumbsItems = [
        {
          title: 'Home',
          href: routePaths.home,
        },
        {
          title: isIULPhase2Enabled ? SUBSCRIPTIONS.ALL_SUBSCRIPTIONS : SUBSCRIPTIONS.HEADING,
          href: routePaths.subscriptions,
        },
      ];

      return (
        <PageBackgroundV2
          heading={isIULPhase2Enabled ? SUBSCRIPTIONS.ALL_SUBSCRIPTIONS : SUBSCRIPTIONS.HEADING}
          breadcrumbsProps={{ items: breadcrumbsItems }}
        >
          {renderAppPage()}
        </PageBackgroundV2>
      );
    }

    return (
      <PageBackground
        heading={SUBSCRIPTIONS.HEADING}
        imageKey={Background.SUBSCRIPTION_BACKGROUND}
        className="page-background gray-bg subscription-page-background-v2"
        actions={renderActions()}
      >
        {renderAppPage()}
      </PageBackground>
    );
  };

  return renderPage();
};
